<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>
  <div class="page-wrapper">
    <div class="content">
      <div class="page-header">
        <div class="page-title">
          <h4>{{ $t('documentSettings.title') }}</h4>
          <h6>{{ $t('documentSettings.subtitle') }}</h6>
        </div>
        <div class="page-btn">
          <router-link to="/societe" class="btn btn-secondary">
            <vue-feather type="arrow-left" class="me-2"></vue-feather>
            {{ $t('documentSettings.back') }}
          </router-link>
        </div>
      </div>

      <div class="row g-4">
        <div class="col-xl-5">
          <div class="card">
            <div class="card-body">
              <form @submit.prevent="saveConfiguration">
                <div class="nav nav-tabs mb-4">
                  <button type="button" class="nav-link" :class="{ active: activeTab === 'header' }" @click="activeTab = 'header'">{{ $t('documentSettings.headerTab') }}</button>
                  <button type="button" class="nav-link" :class="{ active: activeTab === 'footer' }" @click="activeTab = 'footer'">{{ $t('documentSettings.footerTab') }}</button>
                </div>
                <div v-if="loading" class="text-center py-4"><span class="spinner-border spinner-border-sm me-2"></span>{{ $t('documentSettings.loading') }}</div>
                <template v-if="!loading && activeTab === 'header'">
                <div class="mb-4 text-center">
                  <img v-if="logoUrl" :src="logoUrl" class="document-logo mb-2" alt="Logo société" />
                  <div class="fw-semibold">{{ company.societe_nom || 'Société' }}</div>
                  <small class="text-muted">{{ company.email || '' }}</small>
                </div>
                <div class="mb-4">
                  <label class="form-label">{{ $t('documentSettings.logoPosition') }}</label>
                  <div class="position-control">
                    <button
                      v-for="option in logoPositionOptions"
                      :key="option.value"
                      type="button"
                      class="position-option"
                      :class="{ active: form.document_logo_position === option.value }"
                      @click="form.document_logo_position = option.value"
                    >
                      <vue-feather :type="option.icon" size="16"></vue-feather>
                      <span>{{ $t(`documentSettings.position.${option.value}`) }}</span>
                    </button>
                  </div>
                </div>
                </template>

                <template v-if="!loading && activeTab === 'footer'">
                <div class="mb-4">
                  <label class="form-label">{{ $t('documentSettings.customText') }}</label>
                  <textarea
                    v-model.trim="form.document_footer_text"
                    class="form-control footer-text-input"
                    rows="4"
                    maxlength="500"
                    :placeholder="$t('documentSettings.customTextPlaceholder')"
                  ></textarea>
                  <div class="form-text text-end">
                    {{ (form.document_footer_text || "").length }}/500
                  </div>
                </div>

                <div class="mb-4">
                  <label class="form-label">{{ $t('documentSettings.alignment') }}</label>
                  <div class="position-control">
                    <button
                      v-for="option in textAlignOptions"
                      :key="option.value"
                      type="button"
                      class="position-option"
                      :class="{ active: textAlignment === option.value }"
                      @click="textAlignment = option.value"
                    >
                      <vue-feather :type="option.icon" size="16"></vue-feather>
                      <span>{{ $t(`documentSettings.position.${option.value}`) }}</span>
                    </button>
                  </div>
                </div>

                <div class="mb-4">
                  <label class="form-label">{{ $t('documentSettings.verticalPosition') }}</label>
                  <div class="position-control">
                    <button
                      v-for="option in textVerticalOptions"
                      :key="option.value"
                      type="button"
                      class="position-option"
                      :class="{ active: textVerticalPosition === option.value }"
                      @click="textVerticalPosition = option.value"
                    >
                      <vue-feather :type="option.icon" size="16"></vue-feather>
                      <span>{{ $t(`documentSettings.position.${option.value}`) }}</span>
                    </button>
                  </div>
                </div>

                <div class="mb-4">
                  <label class="form-label">{{ $t('documentSettings.textSize') }}</label>
                  <div class="position-control mb-2">
                    <button
                      v-for="option in textSizeOptions"
                      :key="option.value"
                      type="button"
                      class="position-option"
                      :class="{ active: textSize === option.value }"
                      @click="setTextSize(option.value)"
                    >
                      <span>{{ $t(`documentSettings.size.${option.value}`) }}</span>
                    </button>
                  </div>
                  <div class="text-size-control">
                    <input
                      v-model.number="customTextSize"
                      type="number"
                      class="form-control"
                      min="6"
                      max="16"
                      step="1"
                      placeholder="16"
                      @input="textSize = 'custom'"
                    />
                    <span class="text-size-unit">px</span>
                  </div>
                </div>

                <div class="mb-4">
                  <label class="form-label">{{ $t('documentSettings.font') }}</label>
                  <select v-model="textFontFamily" class="form-control">
                    <option
                      v-for="option in textFontOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>

                <div class="mb-4">
                  <label class="form-label">{{ $t('documentSettings.style') }}</label>
                  <div class="position-control">
                    <button
                      v-for="option in textWeightOptions"
                      :key="option.value"
                      type="button"
                      class="position-option"
                      :class="{ active: textWeight === option.value }"
                      @click="textWeight = option.value"
                    >
                      <span>{{ $t(`documentSettings.weight.${option.value}`) }}</span>
                    </button>
                  </div>
                </div>

                <div class="mb-4">
                  <label class="form-label">{{ $t('documentSettings.color') }}</label>
                  <div class="color-control">
                    <input
                      v-model="form.document_footer_text_color"
                      type="color"
                      class="form-control form-control-color"
                      title="Choisir une couleur"
                    />
                    <input
                      v-model.trim="form.document_footer_text_color"
                      type="text"
                      class="form-control"
                      maxlength="20"
                      placeholder="#6B7280"
                    />
                  </div>
                </div>

                <div class="mb-4">
                  <label class="form-label">{{ $t('documentSettings.companyFields') }}</label>
                  <div class="row g-2">
                    <div v-for="option in footerFieldOptions" :key="option.value" class="col-6">
                      <label class="footer-field-option">
                        <input v-model="footerFields" type="checkbox" :value="option.value" class="form-check-input" />
                        <span><strong>{{ $t(`documentSettings.fields.${option.value}`) }}</strong><small class="d-block text-muted">{{ footerFieldValue(option.value) }}</small></span>
                      </label>
                    </div>
                  </div>
                </div>
                </template>

                <div class="mb-4">
                  <label class="form-label">{{ $t('documentSettings.downloadFormat') }}</label>
                  <div class="position-control">
                    <button
                      v-for="option in downloadFormatOptions"
                      :key="option.value"
                      type="button"
                      class="position-option"
                      :class="{ active: downloadFormat === option.value }"
                      @click="downloadFormat = option.value"
                    >
                      <vue-feather :type="option.icon" size="16"></vue-feather>
                      <span>{{ option.label }}</span>
                    </button>
                  </div>
                </div>

                <div v-if="errorMessage" class="alert alert-danger py-2">
                  {{ errorMessage }}
                </div>

                <button type="submit" class="btn btn-submit w-100" :disabled="saving">
                  <span
                    v-if="saving"
                    class="spinner-border spinner-border-sm me-2"
                  ></span>
                  {{ saving ? $t('documentSettings.saving') : $t('documentSettings.save') }}
                </button>
                <button
                  type="button"
                  class="btn btn-outline-primary w-100 mt-2"
                  :disabled="saving"
                  @click="downloadDocument"
                >
                  {{ downloadButtonLabel }}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div class="col-xl-7">
          <div class="document-preview-shell">
            <div ref="documentPreview" class="document-preview">
              <div class="document-header" :class="logoPositionClass">
                <img
                  v-if="logoUrl"
                  :src="logoUrl"
                  class="document-logo"
                  alt="Logo société"
                />
                <div v-else class="document-logo-placeholder">
                  {{ companyInitials }}
                </div>
              </div>

              <div class="document-body"></div>

              <div
                class="document-footer"
              >
                <div
                  v-if="pageTextPreview"
                  class="document-footer-line custom-footer-line"
                  :style="{ color: normalizedFooterColor }"
                >
                  {{ pageTextPreview }}
                </div>
                <div
                  v-for="line in defaultFooterLines"
                  :key="line"
                  class="document-footer-line"
                >
                  {{ line }}
                </div>
                <div class="document-page-number">1/1</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getCompanyData,
  getCompanyLogoDataUrl,
  getDocumentSettings,
  updateDocumentSettings,
} from "@/services/companyService";
import { jsPDF } from "jspdf";
import { toast } from "vue3-toastify";

const APP_URL = (process.env.VUE_APP_URL || "").replace(/\/$/, "");
const DEFAULT_LOGO = require("@/assets/img/logo.png");

export default {
  data() {
    return {
      saving: false,
      loading: false,
      errorMessage: "",
      company: {},
      logoDataUrl: "",
      activeTab: "header",
      footerFields: ["company_name", "capital", "rc", "tp", "if", "ice", "address", "phone", "email", "website"],
      textAlignment: "center",
      textVerticalPosition: "center",
      textSize: "medium",
      customTextSize: 16,
      textFontFamily: "Roboto",
      textWeight: "400",
      downloadFormat: "png",
      form: {
        document_logo_position: "center",
        document_footer_text: "",
        document_footer_text_color: "#6B7280",
      },
      logoPositionOptions: [
        { value: "left", label: "Gauche", icon: "align-left" },
        { value: "center", label: "Centre", icon: "align-center" },
        { value: "right", label: "Droite", icon: "align-right" },
      ],
      textAlignOptions: [
        { value: "left", label: "Gauche", icon: "align-left" },
        { value: "center", label: "Centre", icon: "align-center" },
        { value: "right", label: "Droite", icon: "align-right" },
      ],
      textVerticalOptions: [
        { value: "top", label: "Haut", icon: "arrow-up" },
        { value: "center", label: "Centre", icon: "minus" },
        { value: "bottom", label: "Bas", icon: "arrow-down" },
      ],
      textSizeOptions: [
        { value: "small", label: "Petit" },
        { value: "medium", label: "Normal" },
        { value: "large", label: "Grand" },
      ],
      textFontOptions: [
        { value: "Roboto", label: "Roboto" },
        { value: "Helvetica", label: "Helvetica" },
        { value: "Times", label: "Times" },
        { value: "Courier", label: "Courier" },
      ],
      textWeightOptions: [
        { value: "400", label: "Regular" },
        { value: "700", label: "Bold" },
      ],
      downloadFormatOptions: [
        { value: "png", label: "Image", icon: "image" },
        { value: "pdf", label: "PDF", icon: "file-text" },
        { value: "doc", label: "DOC", icon: "file" },
      ],
      footerFieldOptions: [
        { value: "company_name", label: "Nom de société" },
        { value: "capital", label: "Capital" },
        { value: "rc", label: "RC" },
        { value: "tp", label: "Patente" },
        { value: "if", label: "Identifiant fiscal" },
        { value: "ice", label: "ICE" },
        { value: "address", label: "Adresse" },
        { value: "phone", label: "Téléphone" },
        { value: "email", label: "Email" },
        { value: "website", label: "Site web" },
      ],
    };
  },
  computed: {
    logoUrl() {
      if (this.logoDataUrl) return this.logoDataUrl;
      const logo = this.company.logo || this.company.logo_small;
      if (!logo) return "";
      if (/^https?:\/\//i.test(logo)) return logo;
      const base = APP_URL || window.location.origin;
      return `${base}/storage/${logo}`;
    },
    logoPositionClass() {
      return `logo-${this.form.document_logo_position || "center"}`;
    },
    textAlignmentClass() {
      return `text-${this.textAlignment || "center"}`;
    },
    textVerticalClass() {
      return `vertical-${this.textVerticalPosition || "center"}`;
    },
    textFontSize() {
      if (this.textSize === "custom") {
        const size = Number(this.customTextSize);
        if (Number.isFinite(size)) {
          return Math.min(Math.max(size, 6), 16);
        }
      }

      const sizes = {
        small: 7,
        medium: 8,
        large: 10,
      };
      return sizes[this.textSize] || sizes.medium;
    },
    safeTextFontFamily() {
      const allowed = this.textFontOptions.map((option) => option.value);
      return allowed.includes(this.textFontFamily) ? this.textFontFamily : "Roboto";
    },
    canvasTextFontFamily() {
      return this.safeTextFontFamily.includes(" ")
        ? `"${this.safeTextFontFamily}", sans-serif`
        : `${this.safeTextFontFamily}, sans-serif`;
    },
    normalizedTextWeight() {
      return this.textWeight === "700" ? "700" : "400";
    },
    pageTextStyle() {
      return {
        color: this.normalizedFooterColor,
        fontSize: `${this.textFontSize}px`,
        fontFamily: this.safeTextFontFamily,
        fontWeight: this.normalizedTextWeight,
      };
    },
    downloadButtonLabel() {
      return this.$t(`documentSettings.download.${this.downloadFormat || "png"}`);
    },
    normalizedFooterColor() {
      const value = this.form.document_footer_text_color || "#6B7280";
      return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value)
        ? value
        : "#6B7280";
    },
    pageTextPreview() {
      return this.form.document_footer_text || "";
    },
    defaultFooterLines() {
      const selected = new Set(this.footerFields);
      return [
        [selected.has("company_name") ? this.company.societe_nom : "", selected.has("capital") ? `Capital : ${this.company.capital || "-"} MAD` : ""],
        [selected.has("rc") ? `RC : ${this.company.rc || "-"}` : "", selected.has("tp") ? `Patente : ${this.company.tp || "-"}` : "", selected.has("if") ? `IF : ${this.company.identifiant_fiscal || "-"}` : "", selected.has("ice") ? `ICE : ${this.company.ice || "-"}` : ""],
        [selected.has("address") ? (this.company.adresse_siege_social_francais || this.company.adress || "-") : "", selected.has("phone") ? `Tél : ${this.company.telephone_entreprise || this.company.telephone_personnel || "-"}` : ""],
        [selected.has("email") ? `Email : ${this.company.email || "-"}` : "", selected.has("website") ? `Site Web : ${this.company.url || "-"}` : ""],
      ].map((parts) => parts.filter(Boolean).join(" - ")).filter(Boolean);
    },
    companyInitials() {
      const name = this.company.societe_nom || "Société";
      return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
    },
  },
  methods: {
    async loadCompany() {
      try {
        this.loading = true;
        const response = await getCompanyData();
        const company = response?.data || {};
        this.company = company;
        this.form.document_logo_position =
          company.document_logo_position || "center";
        this.form.document_footer_text = company.document_footer_text || "";
        this.form.document_footer_text_color =
          company.document_footer_text_color || "#6B7280";
        const settingsResponse = await getDocumentSettings();
        const settings = settingsResponse?.data || {};
        this.form.document_logo_position = settings.logo_position || this.form.document_logo_position;
        this.form.document_footer_text = settings.footer_text ?? this.form.document_footer_text;
        this.form.document_footer_text_color = settings.footer_text_color || this.form.document_footer_text_color;
        this.textAlignment = settings.footer_alignment || "center";
        this.customTextSize = Number(settings.footer_font_size || 8);
        this.textSize = "custom";
        this.textFontFamily = settings.footer_font_family || "Roboto";
        this.textWeight = String(settings.footer_font_weight || 400);
        this.footerFields = Array.isArray(settings.footer_fields) ? settings.footer_fields : this.footerFields;
        await this.loadCompanyLogo();
      } catch (error) {
        console.error(error);
        toast.error(
          error?.response?.data?.message ||
            this.$t("documentSettings.loadError")
        );
      } finally {
        this.loading = false;
      }
    },
    async loadCompanyLogo() {
      if (!this.company.logo && !this.company.logo_small) {
        this.logoDataUrl = DEFAULT_LOGO;
        return;
      }

      try {
        this.logoDataUrl = await getCompanyLogoDataUrl("logo");
      } catch (error) {
        console.error(error);
        this.logoDataUrl = "";
      }
    },
    setTextSize(size) {
      this.textSize = size;
      const sizes = {
        small: 7,
        medium: 8,
        large: 10,
      };
      this.customTextSize = sizes[size] || this.customTextSize;
    },
    footerFieldValue(field) {
      const values = {
        company_name: this.company.societe_nom, capital: this.company.capital,
        rc: this.company.rc, tp: this.company.tp, if: this.company.identifiant_fiscal,
        ice: this.company.ice, address: this.company.adresse_siege_social_francais || this.company.adress,
        phone: this.company.telephone_entreprise || this.company.telephone_personnel,
        email: this.company.email, website: this.company.url,
      };
      return values[field] || this.$t("documentSettings.noValue");
    },
    buildDownloadFilename(extension = "png") {
      const name = this.company.societe_nom || "societe";
      const slug = name
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

      return `${slug || "societe"}-mise-en-page.${extension}`;
    },
    loadImageForCanvas(src) {
      return new Promise((resolve, reject) => {
        if (!src) {
          resolve(null);
          return;
        }

        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
      });
    },
    drawWrappedText(context, text, x, y, maxWidth, lineHeight) {
      const paragraphs = String(text || "").split(/\r?\n/);
      let currentY = y;

      paragraphs.forEach((paragraph, paragraphIndex) => {
        const words = paragraph.split(/(\s+)/).filter((part) => part.length);
        let line = "";

        if (!words.length) {
          currentY += lineHeight;
          return;
        }

        words.forEach((word) => {
          const testLine = line ? `${line}${word}` : word;
          if (context.measureText(word).width > maxWidth) {
            if (line.trim()) {
              context.fillText(line.trimEnd(), x, currentY);
              currentY += lineHeight;
              line = "";
            }

            let chunk = "";
            Array.from(word).forEach((char) => {
              const testChunk = `${chunk}${char}`;
              if (context.measureText(testChunk).width > maxWidth && chunk) {
                context.fillText(chunk, x, currentY);
                currentY += lineHeight;
                chunk = char;
              } else {
                chunk = testChunk;
              }
            });
            line = chunk;
          } else if (context.measureText(testLine).width > maxWidth && line.trim()) {
            context.fillText(line.trimEnd(), x, currentY);
            line = word.trimStart();
            currentY += lineHeight;
          } else {
            line = testLine;
          }
        });

        if (line) {
          context.fillText(line.trimEnd(), x, currentY);
          currentY += lineHeight;
        }

        if (paragraphIndex < paragraphs.length - 1) {
          currentY += lineHeight * 0.4;
        }
      });

      return currentY;
    },
    getWrappedLineCount(context, text, maxWidth) {
      const paragraphs = String(text || "").split(/\r?\n/);
      let count = 0;

      paragraphs.forEach((paragraph) => {
        const words = paragraph.split(/(\s+)/).filter((part) => part.length);
        let line = "";

        if (!words.length) {
          count += 1;
          return;
        }

        words.forEach((word) => {
          const testLine = line ? `${line}${word}` : word;
          if (context.measureText(word).width > maxWidth) {
            if (line.trim()) {
              count += 1;
              line = "";
            }

            let chunk = "";
            Array.from(word).forEach((char) => {
              const testChunk = `${chunk}${char}`;
              if (context.measureText(testChunk).width > maxWidth && chunk) {
                count += 1;
                chunk = char;
              } else {
                chunk = testChunk;
              }
            });
            line = chunk;
          } else if (context.measureText(testLine).width > maxWidth && line.trim()) {
            count += 1;
            line = word.trimStart();
          } else {
            line = testLine;
          }
        });

        if (line) count += 1;
      });

      return Math.max(count, 1);
    },
    async renderDocumentCanvas(options = {}) {
      await this.$nextTick();

      const preview = this.$refs.documentPreview;
      if (!preview) {
        throw new Error("Aperçu introuvable");
      }

      const rect = preview.getBoundingClientRect();
      const width = Math.round(rect.width || 560);
      const height = Math.round(rect.height || 792);
      const scale = options.scale || Math.min(window.devicePixelRatio || 2, 3);
      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = height * scale;
      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("Canvas indisponible");
      }

      context.setTransform(scale, 0, 0, scale, 0, 0);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, width, height);
      context.strokeStyle = "#e5e7eb";
      context.lineWidth = 1;
      context.strokeRect(0.5, 0.5, width - 1, height - 1);

      const padding = 32;
      const logoImage = await this.loadImageForCanvas(this.logoUrl).catch(() => null);
      if (logoImage) {
        const maxLogoWidth = 150;
        const maxLogoHeight = 62;
        const logoRatio = Math.min(
          maxLogoWidth / logoImage.naturalWidth,
          maxLogoHeight / logoImage.naturalHeight,
          1
        );
        const logoWidth = logoImage.naturalWidth * logoRatio;
        const logoHeight = logoImage.naturalHeight * logoRatio;
        let logoX = padding;
        if (this.form.document_logo_position === "center") {
          logoX = (width - logoWidth) / 2;
        } else if (this.form.document_logo_position === "right") {
          logoX = width - padding - logoWidth;
        }
        context.drawImage(logoImage, logoX, padding, logoWidth, logoHeight);
      } else {
        const boxWidth = 118;
        const boxHeight = 56;
        let boxX = padding;
        if (this.form.document_logo_position === "center") {
          boxX = (width - boxWidth) / 2;
        } else if (this.form.document_logo_position === "right") {
          boxX = width - padding - boxWidth;
        }
        context.fillStyle = "#f8fafc";
        context.strokeStyle = "#cbd5e1";
        context.strokeRect(boxX, padding, boxWidth, boxHeight);
        context.fillRect(boxX, padding, boxWidth, boxHeight);
        context.fillStyle = "#334155";
        context.font = "700 16px Arial, sans-serif";
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.fillText(this.companyInitials, boxX + boxWidth / 2, padding + boxHeight / 2);
      }

      const maxTextWidth = width - padding * 2 - 24;
      const pageText = this.pageTextPreview;
      if (pageText) {
        context.fillStyle = this.normalizedFooterColor;
        context.font = `${this.normalizedTextWeight} ${this.textFontSize}px ${this.canvasTextFontFamily}`;
        context.textBaseline = "top";
        context.textAlign = this.textAlignment;
        const lineHeight = Math.round(this.textFontSize * 1.45);
        const lineCount = this.getWrappedLineCount(context, pageText, maxTextWidth);
        const textBlockHeight = lineCount * lineHeight;
        const footerTop = height - padding - 58;
        const textY =
          this.textVerticalPosition === "top"
            ? padding + 96
            : this.textVerticalPosition === "bottom"
              ? footerTop - textBlockHeight - 28
              : height * 0.52 - textBlockHeight / 2;
        const textX =
          this.textAlignment === "left"
            ? padding + 12
            : this.textAlignment === "right"
              ? width - padding - 12
              : width / 2;
        this.drawWrappedText(context, pageText, textX, textY, maxTextWidth, lineHeight);
      }

      const footerTop = height - padding - 58;
      context.strokeStyle = "#e5e7eb";
      context.beginPath();
      context.moveTo(padding, footerTop);
      context.lineTo(width - padding, footerTop);
      context.stroke();

      context.fillStyle = "#6B7280";
      context.font = "8px Arial, sans-serif";
      context.textAlign = "center";
      context.textBaseline = "top";
      let footerY = footerTop + 8;
      this.defaultFooterLines.forEach((line) => {
        footerY = this.drawWrappedText(
          context,
          line,
          width / 2,
          footerY,
          width - padding * 2,
          11
        );
      });
      context.fillStyle = "#999999";
      context.textAlign = "right";
      context.fillText("1/1", width - padding, footerTop + 42);

      return canvas;
    },
    downloadCanvasAsImage(canvas) {
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = this.buildDownloadFilename("png");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Image téléchargée.");
    },
    downloadCanvasAsPdf(canvas) {
      const imageData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4",
        compress: false,
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(imageData, "PNG", 0, 0, pageWidth, pageHeight);
      pdf.save(this.buildDownloadFilename("pdf"));
      toast.success("PDF téléchargé.");
    },
    downloadCanvasAsDoc(canvas) {
      const imageData = canvas.toDataURL("image/png");
      const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    @page { size: A4; margin: 0; }
    body { margin: 0; padding: 0; }
    img { display: block; width: 100%; height: auto; }
  </style>
</head>
<body>
  <img src="${imageData}" alt="Document">
</body>
</html>`;
      const blob = new Blob(["\ufeff", html], { type: "application/msword" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = this.buildDownloadFilename("doc");
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(link.href);
      link.remove();
      toast.success("DOC téléchargé.");
    },
    async downloadDocument() {
      this.errorMessage = "";
      const rawColor = this.form.document_footer_text_color || "";

      if (!/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(rawColor)) {
        this.errorMessage = this.$t("documentSettings.colorError");
        return;
      }

      try {
        this.saving = true;
        const canvas = await this.renderDocumentCanvas({
          scale: this.downloadFormat === "pdf" ? 5 : undefined,
        });
        if (this.downloadFormat === "pdf") {
          this.downloadCanvasAsPdf(canvas);
        } else if (this.downloadFormat === "doc") {
          this.downloadCanvasAsDoc(canvas);
        } else {
          this.downloadCanvasAsImage(canvas);
        }
      } catch (error) {
        console.error(error);
        this.errorMessage =
          error?.message || "Erreur lors du téléchargement du document.";
      } finally {
        this.saving = false;
      }
    },
    async saveConfiguration() {
      this.errorMessage = "";
      const rawColor = this.form.document_footer_text_color || "";

      if (!/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(rawColor)) {
        this.errorMessage = this.$t("documentSettings.colorError");
        return;
      }

      try {
        this.saving = true;
        const response = await updateDocumentSettings({
          logo_position: this.form.document_logo_position,
          footer_text: this.form.document_footer_text || "",
          footer_text_color: rawColor,
          footer_alignment: this.textAlignment,
          footer_font_size: this.textFontSize,
          footer_font_family: this.textFontFamily,
          footer_font_weight: Number(this.textWeight),
          footer_fields: this.footerFields,
        });
        const savedSettings = response?.data || {};
        this.form.document_logo_position = savedSettings.logo_position;
        this.form.document_footer_text = savedSettings.footer_text || "";
        this.form.document_footer_text_color = savedSettings.footer_text_color;
        toast.success(this.$t("documentSettings.saved"));
      } catch (error) {
        console.error(error);
        const errors = error?.response?.data?.errors;
        this.errorMessage = errors
          ? Object.values(errors).flat().join(" ")
          : error?.response?.data?.message || this.$t("documentSettings.saveError");
      } finally {
        this.saving = false;
      }
    },
  },
  mounted() {
    this.loadCompany();
  },
};
</script>

<style scoped>
.position-control {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.position-option {
  min-height: 44px;
  border: 1px solid #d9dee8;
  background: #fff;
  color: #1f2937;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 600;
}

.position-option.active {
  border-color: #1e68a3;
  background: #eef6ff;
  color: #1e4f7a;
}

.color-control {
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 10px;
}

.footer-field-option {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-height: 62px;
  padding: 10px;
  border: 1px solid #d9dee8;
  border-radius: 6px;
  cursor: pointer;
}

.text-size-control {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
}

.text-size-unit {
  color: #6b7280;
  font-weight: 600;
}

.document-preview-shell {
  background: #eef1f5;
  border: 1px solid #dde3eb;
  border-radius: 8px;
  padding: 24px;
}

.document-preview {
  width: min(100%, 560px);
  aspect-ratio: 210 / 297;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
  padding: 32px;
  padding-bottom: 12px;
  display: flex;
  flex-direction: column;
  position: relative;
}

.document-header {
  min-height: 78px;
  display: flex;
  align-items: flex-start;
}

.document-header.logo-left {
  justify-content: flex-start;
}

.document-header.logo-center {
  justify-content: center;
}

.document-header.logo-right {
  justify-content: flex-end;
}

.document-logo {
  max-width: 150px;
  max-height: 62px;
  object-fit: contain;
}

.document-logo-placeholder {
  width: 118px;
  height: 56px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #334155;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.document-body {
  flex: 1;
  background: #fff;
}

.document-page-text {
  position: absolute;
  left: 32px;
  right: 32px;
  top: 52%;
  transform: translateY(-50%);
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: pre-line;
  overflow-wrap: anywhere;
  word-break: break-word;
  font-size: 16px;
  line-height: 1.45;
  font-weight: 600;
  padding: 0 12px;
}

.document-page-text.text-left {
  justify-content: flex-start;
  text-align: left;
}

.document-page-text.text-center {
  justify-content: center;
  text-align: center;
}

.document-page-text.text-right {
  justify-content: flex-end;
  text-align: right;
}

.document-page-text.vertical-top {
  top: 128px;
  transform: none;
}

.document-page-text.vertical-center {
  top: 52%;
  transform: translateY(-50%);
}

.document-page-text.vertical-bottom {
  top: auto;
  bottom: 118px;
  transform: none;
}

.document-footer {
  border-top: 1px solid #e5e7eb;
  padding-top: 8px;
  font-size: 8px;
  line-height: 1.35;
  text-align: center;
  align-self: center;
  width: 100%;
  min-height: 58px;
  color: #6B7280;
  word-break: break-word;
}

.document-footer-line {
  margin-bottom: 2px;
}

.document-page-number {
  margin-top: 4px;
  text-align: right;
  color: #999;
}

.footer-text-input {
  text-align: center;
}
</style>
